import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { toaster } from 'components/v2/toast';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { ConfirmWithCommentPayload } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { DepublishWithReasonSelectionPayload } from 'components/v2/dialogs/DepublishAdminDialog';
import { EpdUpdateType } from 'components/v2/dialogs/UpdateEpdTypeSelectionDialog';
import AuthService from 'services/AuthService';
import { getEPDPublicationProcessingTimeFrame } from 'services/api/epdApi';
import {
  deleteDocument,
  postCancelDocumentPublication,
  postCancelDocumentUpdate,
  postCancelDocumentVerification,
  postDeclineDocumentPublication,
  postDeclineDocumentVerification,
  postDepublishDocument,
  postDeregisterDocument,
  postGenerateDocumentRegistrationCertificate,
  postLicenseeAdminSendDocumentToPublish,
  postSendDocumentForPublicationEditorial,
  postStartDocumentEditorialUpdate,
  postStartDocumentUpdate,
} from 'services/api/dynamicFormsApi';
import { useDocumentStore } from './useDocumentStore';
import EpdRoutes from 'routes/EpdRoutes';
import SendForPublishSuccessToast from '../components/SendForPublishSuccessToast';

export type DocumentDialog =
  | 'declinePublication'
  | 'declineVerification'
  | 'editorialSendToPublish'
  | 'licenseeAdminReview'
  | 'depublishAdmin'
  | 'startUpdate';

export function useDocumentActions() {
  const { docId, metadata, synchronize, reloadData } = useDocumentStore();
  const { licensee } = useContext(CompanyContext);
  const history = useHistory();
  const queryClient = useQueryClient();

  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
  const [openDialog, setOpenDialog] = useState<DocumentDialog | null>(null);
  // Seeded with 'update' so an option is always preselected and the dialog's Send button is always
  // enabled — the shipped Simple Wizard behaviour (pages/wizard/ToolButtons.tsx), required by FR-003.
  // onStartUpdate re-seeds it on every open; see the comment there.
  const [updateType, setUpdateType] = useState<EpdUpdateType>('update');

  const doAction = async (
    operationCallback: () => Promise<void>,
    shouldReloadData: boolean = true,
    onSuccess?: () => Promise<void>
  ) => {
    try {
      setIsSpinnerVisible(true);
      await synchronize(operationCallback);
      await onSuccess?.();
    } catch (error) {
      let details = 'An error occurred while performing the operation. Please try again.';
      if (isAxiosError(error) && error.response?.data?.message) {
        details = error.response.data.message;
      }
      console.error('Error during operation', error);
      toaster({ severity: 'error', summary: 'Operation failed', details });
    } finally {
      // State and comment may already be committed when a subsequent notification fails.
      // Without a reload the page is leaving (the document was deleted), so there is no log to refetch.
      if (shouldReloadData) {
        await queryClient.invalidateQueries({ queryKey: ['DOC_COMMENT_LOG', docId] });
        reloadData();
      }
      setIsSpinnerVisible(false);
    }
  };

  const showSendForPublishSuccessToast = async () => {
    const timeframe = await getEPDPublicationProcessingTimeFrame();
    const hasLicensee = licensee?.name !== 'defaultLicensee';
    toast.success(
      React.createElement(SendForPublishSuccessToast, {
        minDays: timeframe?.minProcessingDays,
        maxDays: timeframe?.maxProcessingDays,
        hasLicensee,
      })
    );
  };

  const onDeclinePublicationConfirmed = ({ commentText }: ConfirmWithCommentPayload) => {
    setOpenDialog(null);
    doAction(() => postDeclineDocumentPublication(docId, commentText));
  };

  const onDeclineVerificationConfirmed = (payload: ConfirmWithCommentPayload | null) => {
    setOpenDialog(null);
    if (payload) {
      doAction(() => postDeclineDocumentVerification(docId, payload.commentText));
    }
  };

  const onCancelVerification = () => {
    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmAlert({
      caption: `Are you sure you want to cancel the verification of ${epdName}?`,
      description: React.createElement('div', null, 'This action may interrupt Step 3 - EPD Verification process.'),
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      onConfirm: () => doAction(() => postCancelDocumentVerification(docId)),
    });
  };

  const onCancelUpdate = () => {
    // Cancelling an update is an EPD Owner action. Show the owner-only error immediately — before the
    // confirmation dialog — for other roles (e.g. the LCA Practitioner), rather than asking them to
    // confirm an action they can't perform. The backend enforces the same rule (403) as a safeguard.
    const hasPermissions = AuthService.isAdmin() || AuthService.isCompanyOwner(metadata.companyId);
    if (!hasPermissions) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: 'Only the EPD Owner is allowed to cancel the EPD update.',
      });
      return;
    }

    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmAlert({
      caption: `Are you sure you want to cancel the update of ${epdName}?`,
      description: React.createElement('div', null, 'All added changes will be lost.'),
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      onConfirm: () => doAction(() => postCancelDocumentUpdate(docId)),
    });
  };

  const onCancelPublication = () => {
    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmAlert({
      caption: `Are you sure you want to cancel the publication of ${epdName}?`,
      description: 'You may interrupt the EPD reviewing process.',
      acceptLabel: 'Submit',
      onConfirm: () => doAction(() => postCancelDocumentPublication(docId)),
    });
  };

  const onDeregister = () => {
    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmAlert({
      caption: `Are you sure you want to deregister ${epdName}?`,
      description: 'The EPD will be deregistered and shall no longer be used or referred to.',
      acceptLabel: 'Deregister',
      rejectLabel: 'Cancel',
      onConfirm: () => doAction(() => postDeregisterDocument(docId)),
    });
  };

  const onEditorialSendToPublishConfirmed = (payload: ConfirmWithCommentPayload | null) => {
    setOpenDialog(null);
    if (payload) {
      doAction(
        () => postSendDocumentForPublicationEditorial(docId, payload.commentText),
        true,
        showSendForPublishSuccessToast
      );
    }
  };

  // Single entry point for the Update EPD button: the whole decision lives here, next to the state it
  // reads, instead of being split between the component and this hook.
  // Only an EPD Verification document is asked which kind of update it is. Process Certification and
  // pre-verified tool documents — and a verification type that has not loaded yet (undefined) — start
  // an update directly, exactly as before this dialog existed.
  const onStartUpdate = (isEpdVerification: boolean) => {
    if (!isEpdVerification) {
      doAction(() => postStartDocumentUpdate(docId));
      return;
    }

    // Re-seed on every open. An earlier, abandoned choice must never become the default for the next
    // one: the Send button is always enabled, so a sticky 'editorialUpdate' would be one click away
    // from starting an unverified update on a verified EPD. FR-003 still holds — an option is always
    // preselected, it is simply always the safe one.
    setUpdateType('update');
    setOpenDialog('startUpdate');
  };

  const onStartUpdateConfirmed = () => {
    setOpenDialog(null);
    doAction(() =>
      updateType === 'editorialUpdate' ? postStartDocumentEditorialUpdate(docId) : postStartDocumentUpdate(docId)
    );
  };

  const onLicenseeAdminReviewConfirmed = (payload: ConfirmWithCommentPayload | null) => {
    setOpenDialog(null);
    if (payload) {
      doAction(
        () => postLicenseeAdminSendDocumentToPublish(docId, payload.commentText),
        true,
        showSendForPublishSuccessToast
      );
    }
  };

  const onDepublishByAdminConfirmed = (payload: DepublishWithReasonSelectionPayload | null) => {
    setOpenDialog(null);
    if (payload) {
      doAction(() => postDepublishDocument(docId, payload.commentText, payload.depublishType));
    }
  };

  const onDepublishByCustomer = () => {
    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmAlert({
      caption: `Are you sure you want to depublish ${epdName}?`,
      description: React.createElement(
        'div',
        null,
        'The EPD will be depublished and no longer available on environdec.com.',
        React.createElement('br'),
        ' To publish your EPD again, you will need to contact the Secretariat.'
      ),
      acceptLabel: 'Depublish',
      rejectLabel: 'Cancel',
      onConfirm: () => doAction(() => postDepublishDocument(docId)),
    });
  };

  const onDeleteDraft = () => {
    const epdName = [metadata.fullIdentificationNumber, metadata.title].filter(Boolean).join(' ');
    confirmDelete({
      caption: `Are you sure you want to delete ${epdName}?`,
      description: React.createElement('div', null, 'The EPD data will be lost and you will not be able to restore it.'),
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () =>
        doAction(async () => {
          await deleteDocument(docId);
          history.push(EpdRoutes.dashboard);
        }, false),
    });
  };

  const onGenerateRegistrationCertificate = () => {
    doAction(async () => {
      await postGenerateDocumentRegistrationCertificate(docId);
      toaster({
        severity: 'success',
        summary: 'Registration certificate has been generated successfully!',
        details: 'It is now available on the EPD details page at environdec.com.',
      });
    });
  };

  return {
    doAction,
    showSendForPublishSuccessToast,
    isSpinnerVisible,
    openDialog,
    setOpenDialog,
    onDeclinePublicationConfirmed,
    onDeclineVerificationConfirmed,
    onCancelVerification,
    onCancelUpdate,
    onCancelPublication,
    onDeregister,
    onEditorialSendToPublishConfirmed,
    updateType,
    setUpdateType,
    onStartUpdate,
    onStartUpdateConfirmed,
    onLicenseeAdminReviewConfirmed,
    onDepublishByAdminConfirmed,
    onDepublishByCustomer,
    onDeleteDraft,
    onGenerateRegistrationCertificate,
  };
}
